import React from 'react';
import { MessageSquare, Clock } from 'lucide-react';
import { Chat } from '../types';

interface ChatListItemProps {
  chat: Chat;
  isSelected: boolean;
  onClick: () => void;
  userRole: 'user' | 'provider';
}

const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  if (diffInHours < 24) return `${diffInHours}h ago`;
  if (diffInDays < 7) return `${diffInDays}d ago`;
  return date.toLocaleDateString();
};

export const ChatListItem: React.FC<ChatListItemProps> = ({
  chat,
  isSelected,
  onClick,
  userRole,
}) => {
  const lastMessage = chat.messages[chat.messages.length - 1];
  const isUnread = lastMessage && lastMessage.sender !== userRole;

  const displayName = userRole === 'provider'
    ? chat.userName || 'Service User'
    : chat.providerName || 'Care Provider';

  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-3 sm:p-4 border-b border-gray-200 hover:bg-gray-50 transition-colors ${
        isSelected ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-2 sm:gap-3">
        <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
          <div className={`p-1.5 sm:p-2 rounded-full ${
            userRole === 'provider' ? 'bg-blue-100' : 'bg-green-100'
          } flex-shrink-0`}>
            <MessageSquare className={`h-4 w-4 sm:h-5 sm:w-5 ${
              userRole === 'provider' ? 'text-blue-600' : 'text-green-600'
            }`} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-0.5 sm:mb-1">
              <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-1">
                <h3 className={`font-semibold truncate text-sm sm:text-base ${
                  isUnread ? 'text-gray-900' : 'text-gray-700'
                }`}>
                  {displayName}
                </h3>
                {userRole === 'user' && chat.providerIsBlocked && (
                  <span className="text-xs px-1.5 sm:px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full flex-shrink-0">
                    Inactive
                  </span>
                )}
              </div>
              <div className="flex items-center gap-0.5 sm:gap-1 text-xs text-gray-500 flex-shrink-0">
                <Clock className="h-3 w-3" />
                <span className="whitespace-nowrap">{formatRelativeTime(lastMessage?.createdAt || chat.updatedAt)}</span>
              </div>
            </div>

            <p className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2 truncate">
              {chat.postTitle}
            </p>

            {lastMessage && (
              <p className={`text-xs sm:text-sm truncate ${
                isUnread ? 'text-gray-900 font-medium' : 'text-gray-500'
              }`}>
                {lastMessage.sender === userRole ? 'You: ' : ''}
                {lastMessage.text}
              </p>
            )}
          </div>
        </div>

        {isUnread && (
          <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0 mt-1 sm:mt-2"></div>
        )}
      </div>
    </button>
  );
};
